import React, { useEffect, useMemo, useState } from 'react';
import { Button, Empty, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, MailOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import SubPageLayout from '../helpers/SubPageLayout';
import { getAllUsersForLocalFilter, getUsersList, getUsersLoading } from '../../../features/user.store';
import { toast } from 'react-toastify';
import {
  createEmailSubscription,
  deleteEmailSubscription,
  fetchEmailSubscriptions,
  getEmailSubscriptionLoadingById,
  getEmailSubscriptions,
  getEmailSubscriptionsLoading,
  getEmailSubscriptionSaving,
  sendEmailSubscription,
} from '../../../features/manage-emails.store';

const REPORT_TYPES = [
  { label: 'Service Expiry Report', value: 'SERVICE_EXPIRY_REPORT' },
];

const REGIONS = [
  { label: 'Australia', value: 'AUSTRALIA' },
  { label: 'Johannesburg', value: 'JOHANNESBURG' },
  { label: 'Dallas', value: 'DALLAS' },
];

const getErrorMessage = (err, fallback) => {
  const details = err?.data?.errors || err?.data?.validationErrors || err?.data?.fieldErrors;
  const extraDetails = err?.data?.details;

  const parseDetailItem = (item) => {
    if (typeof item === 'string') return item;
    if (item?.message) return item.message;
    if (item?.detail) return item.detail;
    if (item?.description) return item.description;
    if (item?.field && item?.defaultMessage) return `${item.field}: ${item.defaultMessage}`;
    if (item?.field && item?.message) return `${item.field}: ${item.message}`;
    if (item && typeof item === 'object') return JSON.stringify(item);
    return '';
  };

  if (Array.isArray(extraDetails) && extraDetails.length) {
    const text = parseDetailItem(extraDetails[0]);
    if (text) {
      return `${err?.message || fallback}: ${text}`;
    }
  }

  if (extraDetails && typeof extraDetails === 'object' && !Array.isArray(extraDetails)) {
    const firstKey = Object.keys(extraDetails)[0];
    if (firstKey) {
      const value = extraDetails[firstKey];
      if (Array.isArray(value) && value.length) {
        const text = parseDetailItem(value[0]);
        if (text) return `${err?.message || fallback}: ${text}`;
      }
      const text = parseDetailItem(value);
      if (text) return `${err?.message || fallback}: ${text}`;
    }
  }

  if (Array.isArray(details) && details.length) {
    const text = parseDetailItem(details[0]);
    if (text) return `${err?.message || fallback}: ${text}`;
  }

  if (details && typeof details === 'object') {
    const firstKey = Object.keys(details)[0];
    if (firstKey) {
      const value = details[firstKey];
      if (Array.isArray(value) && value.length) {
        return `${err?.message || fallback}: ${value[0]}`;
      }
      if (typeof value === 'string') {
        return `${err?.message || fallback}: ${value}`;
      }
    }
  }

  return err?.message || err?.data?.message || err?.data?.error || fallback;
};

const toRoleText = (user) => {
  return [
    user?.role?.displayName,
    user?.role?.name,
    user?.role?.type,
    user?.authorities,
  ]
    .filter(Boolean)
    .join(' ')
    .toUpperCase();
};

const isAllowedForEmailReports = (user) => {
  const roleText = toRoleText(user);

  return (
    roleText.includes('MINEARC ROLE') ||
    roleText.includes('SUPER ADMIN ROLE') ||
    roleText.includes('MINEARC_ROLE') ||
    roleText.includes('SUPER_ADMIN_ROLE') ||
    roleText.includes('ROLE_TYPE_MINEARC_ROLE') ||
    roleText.includes('ROLE_TYPE_SUPER_ADMIN_ROLE')
  );
};

const ManageEmails = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const users = useSelector(getUsersList) || [];
  const usersLoading = useSelector(getUsersLoading);
  const subscriptions = useSelector(getEmailSubscriptions);
  const subscriptionsLoading = useSelector(getEmailSubscriptionsLoading);
  const subscriptionSaving = useSelector(getEmailSubscriptionSaving);
  const loadingById = useSelector(getEmailSubscriptionLoadingById);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(undefined);
  const [selectedReportType, setSelectedReportType] = useState(undefined);
  const [selectedRegion, setSelectedRegion] = useState(undefined);

  useEffect(() => {
    dispatch(getAllUsersForLocalFilter());
    dispatch(fetchEmailSubscriptions());
  }, [dispatch]);

  const allowedUsers = useMemo(() => {
    return (users || []).filter(isAllowedForEmailReports);
  }, [users]);

  const allowedUserOptions = useMemo(() => {
    return allowedUsers.map((user) => ({
      label: `${user.fullName || 'Unknown User'} (${user.email || 'No email'})`,
      value: String(user.id),
    }));
  }, [allowedUsers]);

  const selectedUser = useMemo(() => {
    return allowedUsers.find((user) => String(user.id) === String(selectedUserId));
  }, [allowedUsers, selectedUserId]);

  const resetForm = () => {
    setSelectedUserId(undefined);
    setSelectedReportType(undefined);
    setSelectedRegion(undefined);
  };

  const openModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const addSubscription = async () => {
    if (!selectedUser || !selectedReportType || !selectedRegion) {
      toast.error('Please select user, report type, and region.');
      return;
    }

    const duplicate = subscriptions.some(
      (item) =>
        String(item.userId) === String(selectedUser.id) &&
        item.reportType === selectedReportType &&
        item.region === selectedRegion,
    );

    if (duplicate) {
      toast.error('This user is already registered for this report and region.');
      return;
    }

    const nextItem = {
      userId: selectedUser.id,
      reportType: selectedReportType,
      region: selectedRegion,
    };

    console.log('[manage-emails] selected user details', {
      selectedUser,
      selectedUserRole: toRoleText(selectedUser),
      isAllowed: isAllowedForEmailReports(selectedUser),
      nextItem,
    });

    try {
      await dispatch(createEmailSubscription(nextItem)).unwrap();
      toast.success('User added for email reports successfully.');
      closeModal();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Unable to add user for email reports.'));
    }
  };

  const deleteSubscription = async (id) => {
    try {
      await dispatch(deleteEmailSubscription({ id })).unwrap();
      toast.success('Email subscription removed.');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Unable to remove email subscription.'));
    }
  };

  const sendSubscriptionEmail = async (subscription) => {
    const reportType = REPORT_TYPES.find((item) => item.value === subscription?.reportType)?.label || 'Report';
    try {
      await dispatch(sendEmailSubscription({ id: subscription?.id })).unwrap();
      toast.success(`Email sent to ${subscription?.email || 'user'} for ${reportType} (${subscription?.region || '-'})`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Unable to send report email.'));
    }
  };

  const columns = [
    {
      title: 'User Name',
      dataIndex: 'fullName',
      render: (value) => value || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      render: (value) => value || '-',
    },
    {
      title: 'Report Type',
      dataIndex: 'reportType',
      render: (value) => {
        const reportType = REPORT_TYPES.find((item) => item.value === value);
        return <Tag color="blue">{reportType?.label || value}</Tag>;
      },
    },
    {
      title: 'Region',
      dataIndex: 'region',
      render: (value) => <Tag color="green">{value}</Tag>,
    },
    {
      title: 'Action',
      dataIndex: 'id',
      width: 140,
      render: (id, record) => (
        <Space size={6}>
          <Popconfirm
            title="Send report email?"
            description={`Send this report email to ${record?.email || 'this user'}?`}
            okText="Send"
            cancelText="Cancel"
            onConfirm={() => sendSubscriptionEmail(record)}
            placement="top"
          >
            <Tooltip title="Send Email">
              <Button
                type="text"
                icon={<SendOutlined />}
                aria-label="Send email"
                loading={Boolean(loadingById[record?.id])}
                style={styles.actionIconButton}
              />
            </Tooltip>
          </Popconfirm>

          <Popconfirm
            title="Delete subscription?"
            description="This user will no longer receive this report email."
            okText="Delete"
            cancelText="Cancel"
            onConfirm={() => deleteSubscription(id)}
            placement="top"
          >
            <Tooltip title="Remove Subscription">
              <Button
                danger
                type="text"
                icon={<DeleteOutlined />}
                aria-label="Delete subscription"
                loading={Boolean(loadingById[record?.id])}
                style={{ ...styles.actionIconButton, color: '#cf1322' }}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const canSubmit = Boolean(selectedUser && selectedReportType && selectedRegion);

  return (
    <SubPageLayout
      title="Manage Emails"
      subtitle="Configure users receiving report emails by report type and region"
      extra={
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => history.push('/users')}>
            Back to Users
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openModal}>
            Add Users for Email Reports
          </Button>
        </Space>
      }
    >
      <div style={styles.summaryCard}>
        <MailOutlined style={{ color: '#0b6a3b', fontSize: 18 }} />
        <Typography.Text strong style={{ marginLeft: 8 }}>
          Registered Email Recipients: {subscriptions.length}
        </Typography.Text>
      </div>

      <Table
        rowKey="id"
        loading={usersLoading || subscriptionsLoading}
        columns={columns}
        dataSource={subscriptions}
        pagination={{ pageSize: 8 }}
        locale={{
          emptyText: (
            <Empty
              description="No users added yet for report emails. Click ‘Add Users for Email Reports’."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />

      <Modal
        title={
          <div style={styles.modalTitleWrap}>
            <div style={styles.modalTitleIcon}>
              <MailOutlined />
            </div>
            <div>
              <div style={styles.modalTitle}>Add User for Report Emails</div>
              <div style={styles.modalSubtitle}>Create an email subscription by user, report type, and region.</div>
            </div>
          </div>
        }
        visible={isModalOpen}
        onCancel={closeModal}
        width={620}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={closeModal} size="large" style={styles.cancelBtn}>
            Cancel
          </Button>,
          <Button
            key="add"
            type="primary"
            onClick={addSubscription}
            size="large"
            loading={subscriptionSaving}
            disabled={!canSubmit}
            style={styles.addBtn}
          >
            Add
          </Button>,
        ]}
      >
        <div style={styles.formIntro}>Only users with MineARC or Super Admin roles can be assigned.</div>

        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div style={styles.fieldCard}>
            <Typography.Text style={styles.fieldLabel}>Select User</Typography.Text>
            <Select
              showSearch
              size="large"
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Select MineARC Role or Super Admin Role user"
              value={selectedUserId}
              options={allowedUserOptions}
              onChange={setSelectedUserId}
              filterOption={(input, option) =>
                String(option?.label || '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </div>

          {selectedUser && (
            <>
              <div style={styles.fieldCard}>
                <Typography.Text style={styles.fieldLabel}>Report Type</Typography.Text>
                <Select
                  size="large"
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder="Select report type"
                  value={selectedReportType}
                  options={REPORT_TYPES}
                  onChange={setSelectedReportType}
                />
              </div>

              <div style={styles.fieldCard}>
                <Typography.Text style={styles.fieldLabel}>Region</Typography.Text>
                <Select
                  size="large"
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder="Select region"
                  value={selectedRegion}
                  options={REGIONS}
                  onChange={setSelectedRegion}
                />
              </div>
            </>
          )}
        </Space>
      </Modal>
    </SubPageLayout>
  );
};

const styles = {
  summaryCard: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#f2fbf5',
    border: '1px solid #d9f1e2',
    borderRadius: 10,
    padding: '10px 14px',
    marginBottom: 14,
  },
  modalTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  modalTitleIcon: {
    width: 34,
    height: 34,
    borderRadius: 8,
    background: 'rgba(11, 106, 59, 0.12)',
    color: '#0b6a3b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 800,
    lineHeight: 1.15,
    color: '#1f2937',
  },
  modalSubtitle: {
    marginTop: 2,
    fontSize: 12,
    color: '#64748b',
  },
  formIntro: {
    border: '1px solid #e1f2e6',
    background: '#f3fbf5',
    color: '#2f5d40',
    borderRadius: 10,
    padding: '10px 12px',
    marginBottom: 12,
    fontSize: 13,
    fontWeight: 600,
  },
  fieldCard: {
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: 12,
    background: '#ffffff',
  },
  fieldLabel: {
    display: 'block',
    marginBottom: 2,
    color: '#374151',
    fontSize: 13,
    fontWeight: 700,
  },
  cancelBtn: {
    minWidth: 100,
    borderRadius: 8,
  },
  addBtn: {
    minWidth: 110,
    borderRadius: 8,
    fontWeight: 700,
    background: '#0b6a3b',
    borderColor: '#0b6a3b',
  },
  actionIconButton: {
    width: 34,
    height: 34,
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    color: '#0b6a3b',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    background: '#fff',
  },
};

export default ManageEmails;
